/**
 * Helpers for applying GET /v1/config fields in the panel.
 */

import type { WidgetConfig, WidgetFeatures } from '../types/api';
import type { WidgetChannel } from '../types/bridge';

/** Feature flags from config — defaults match MVP (text-only). */
export const DEFAULT_FEATURES: WidgetFeatures = {
  file_upload: false,
  voice: false,
  pre_chat_form: false,
};

export function resolveFeatures(
  features: WidgetFeatures | undefined | null,
): WidgetFeatures {
  return {
    file_upload: Boolean(features?.file_upload),
    voice: Boolean(features?.voice),
    pre_chat_form: Boolean(features?.pre_chat_form),
  };
}

/** Non-empty question strings from GET /v1/config.popular_questions. */
export function resolvePopularQuestions(
  questions: WidgetConfig['popular_questions'] | undefined | null,
): string[] {
  if (!questions?.length) return [];
  return questions
    .map((item) => (item?.question || '').trim())
    .filter(Boolean);
}

/**
 * Whether the host should show the chat bubble.
 * `widget_status` is the first indicator — AND more flags here as they land.
 */
export function isLauncherEnabled(
  config: Pick<WidgetConfig, 'widget_status'>,
): boolean {
  return config.widget_status !== false;
}

/** True when Gateway requires HMAC identity binding. */
export function isIdentityVerificationEnabled(
  value: string | undefined | null,
): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== '' && normalized !== 'disabled' && normalized !== 'off';
}

/**
 * Embedding host origin (customer site), used to trust host postMessages and
 * as the `X-Embed-Origin` header and panel → host postMessage target.
 *
 * Not framed (direct link, native WebView): the panel's own origin.
 * Framed: only browser-set values — `ancestorOrigins`, then the referrer. The
 * loader frames us with `referrerpolicy="origin"`, so a real embed always has
 * one. When the browser hides the parent (`"null"`, no referrer) we return
 * `''` and send no `X-Embed-Origin`, so the gateway refuses the session.
 * Never the page-controlled `?host=` or our own origin: either would let any
 * site run another customer's avatar (AW-12 G-05).
 */
export function resolveHostOrigin(): string {
  if (typeof window === 'undefined') return '';
  if (window.parent === window) return window.location.origin;

  try {
    const ancestors = (
      window as Window & { location: Location & { ancestorOrigins?: DOMStringList } }
    ).location.ancestorOrigins;
    const parent = ancestors?.[0];
    if (parent && parent !== 'null') return new URL(parent).origin;
  } catch {
    // ignore
  }

  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch {
    // ignore
  }

  return '';
}

/**
 * Headers for Gateway allowlist.
 * Web: `X-Embed-Origin` (customer site). Mobile: `X-App-Bundle-Id` (iOS/Android
 * bundle) — never the WebView page origin. Session/chat only: GET /v1/config
 * is public and must stay header-free (CDN-cacheable, no CORS preflight).
 * Session/chat may send `app_bundle_id` in the JSON body instead.
 */
export function embedGatewayHeaders(context: {
  channel: WidgetChannel;
  bundleId?: string;
  origin?: string;
}): Record<string, string> {
  if (context.channel === 'mobile_webview') {
    const bundle = (context.bundleId || '').trim();
    return bundle ? { 'X-App-Bundle-Id': bundle } : {};
  }

  const origin = context.origin ?? resolveHostOrigin();
  if (!origin) return {};
  return { 'X-Embed-Origin': origin };
}

/** @deprecated use embedGatewayHeaders — kept for call sites that only need web origin. */
export function embedOriginHeaders(
  origin: string = resolveHostOrigin(),
): Record<string, string> {
  return embedGatewayHeaders({ channel: 'web_widget', origin });
}
